import { useState, useRef, useEffect } from 'preact/hooks';
import type { ChibiImage } from '../../types';

interface ImageComparisonProps {
  image: ChibiImage;
  mode: 'flip' | 'slider' | 'side-by-side';
  onModeChange?: (mode: 'flip' | 'slider' | 'side-by-side') => void;
}

export function ImageComparison({ image, mode }: ImageComparisonProps) {
  const [isFlipped, setIsFlipped] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset state when image changes
  useEffect(() => {
    setIsFlipped(false);
    setSliderPosition(50);
  }, [image.id]);

  const handleSliderMove = (clientX: number) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    setSliderPosition(Math.min(100, Math.max(0, percentage)));
  };

  const handleMouseDown = () => setIsDragging(true);
  const handleMouseUp = () => setIsDragging(false);
  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) handleSliderMove(e.clientX);
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (isDragging && e.touches[0]) {
      handleSliderMove(e.touches[0].clientX);
    }
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isDragging]);

  // Flip mode (default)
  if (mode === 'flip') {
    return (
      <div 
        class="relative w-full h-full max-w-4xl max-h-[90vh] mx-auto"
        onClick={() => setIsFlipped(!isFlipped)}
      >
        <div class={`relative w-full h-full preserve-3d transition-transform duration-600 cursor-pointer ${isFlipped ? 'rotate-y-180' : ''}`}>
          {/* Chibi side */}
          <div class="absolute inset-0 backface-hidden flex items-center justify-center">
            <img
              src={image.chibi}
              alt={image.title}
              class="max-w-full max-h-full object-contain"
            />
            <div class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4 pointer-events-none">
              <h2 class="text-white text-xl font-bold">{image.title}</h2>
              <p class="text-white/80 text-sm">Tap to see original</p>
            </div>
          </div>

          {/* Original side */}
          <div class="absolute inset-0 backface-hidden rotate-y-180 flex items-center justify-center">
            <img
              src={image.original}
              alt={`Original: ${image.description}`}
              class="max-w-full max-h-full object-contain"
            />
            <div class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4 pointer-events-none">
              <h2 class="text-white text-xl font-bold">Original Photo</h2>
              <p class="text-white/80 text-sm">{image.description}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Slider mode
  if (mode === 'slider') {
    return (
      <div 
        ref={containerRef}
        class="relative w-full h-full max-w-4xl max-h-[90vh] mx-auto overflow-hidden"
      >
        {/* Original image (background) */}
        <div class="absolute inset-0 flex items-center justify-center">
          <img
            src={image.original}
            alt={`Original: ${image.description}`}
            class="max-w-full max-h-full object-contain"
          />
          <div class="absolute top-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
            Original
          </div>
        </div>

        {/* Chibi image (foreground with clip) */}
        <div 
          class="absolute inset-0 flex items-center justify-center"
          style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
        >
          <img
            src={image.chibi}
            alt={image.title}
            class="max-w-full max-h-full object-contain"
          />
          <div class="absolute top-4 left-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
            Chibi
          </div>
        </div>

        {/* Slider handle */}
        <div 
          class="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize"
          style={{ left: `${sliderPosition}%` }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleMouseDown}
        >
          <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center">
            <svg class="w-6 h-6 text-gray-700" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l-7-7zM16 5v14l7-7z"/>
            </svg>
          </div>
        </div>

        {/* Instructions */}
        <div class="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full text-sm">
          Drag slider to compare
        </div>
      </div>
    );
  }

  // Side-by-side mode
  return (
    <div class="relative w-full h-full max-w-6xl max-h-[90vh] mx-auto flex gap-4 p-4">
      {/* Chibi side */}
      <div class="flex-1 relative flex flex-col items-center justify-center">
        <img
          src={image.chibi}
          alt={image.title}
          class="max-w-full max-h-full object-contain"
        />
        <div class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
          <h3 class="text-white text-lg font-bold">Chibi Version</h3>
          <p class="text-white/80 text-sm">{image.title}</p>
        </div>
      </div>

      {/* Divider */}
      <div class="w-px bg-white/20"></div>

      {/* Original side */}
      <div class="flex-1 relative flex flex-col items-center justify-center">
        <img
          src={image.original}
          alt={`Original: ${image.description}`}
          class="max-w-full max-h-full object-contain"
        />
        <div class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
          <h3 class="text-white text-lg font-bold">Original Photo</h3>
          <p class="text-white/80 text-sm">{image.description}</p>
        </div>
      </div>
    </div>
  );
}